import { supabase } from '../supabaseClient'
import { extrairErroEdgeFunction, invokeEdgeFunction, USE_DEV_EDGE_PROXY } from './edgeFunctionInvoke'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://ozttgvcetxxzdqfzeylt.supabase.co'
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || ''

export async function listarVendedoresEspecialistas() {
  const { data, error } = await supabase
    .from('usuarios_sistema')
    .select('id, nome, email, empresa_id, chat_zap')
    .eq('status', 'ativo')
    .eq('chat_zap', 1)
    .order('nome')

  if (error) {
    console.error('[WhatsApp CRM] Erro ao listar vendedores:', error)
    return { success: false, error: error.message, vendedores: [] }
  }

  return { success: true, vendedores: data || [] }
}

export async function listarConversas({ vendedorId = null, isGestor = false } = {}) {
  let query = supabase
    .from('whatsapp_conversas')
    .select(`
      id,
      wa_id,
      telefone,
      contato_nome,
      vendedor_id,
      status,
      last_message_at,
      last_message_preview,
      unread_count,
      first_inbound_at,
      first_response_at,
      created_at
    `)
    .eq('status', 'ativa')
    .order('last_message_at', { ascending: false })

  if (!isGestor && vendedorId) {
    query = query.eq('vendedor_id', vendedorId)
  } else if (isGestor && vendedorId) {
    query = query.eq('vendedor_id', vendedorId)
  }

  const { data, error } = await query

  if (error) {
    console.error('[WhatsApp CRM] Erro ao listar conversas:', error)
    return { success: false, error: error.message, conversas: [] }
  }

  const conversas = await aplicarContatosSalvos(agruparConversasPorContato(data || []))
  const vendedorIds = [...new Set(conversas.map((c) => c.vendedor_id).filter(Boolean))]
  let vendedorMap = {}

  if (vendedorIds.length > 0) {
    const { data: vendedores } = await supabase
      .from('usuarios_sistema')
      .select('id, nome')
      .in('id', vendedorIds)
    vendedorMap = Object.fromEntries((vendedores || []).map((v) => [v.id, v]))
  }

  return {
    success: true,
    conversas: conversas.map((c) => ({
      ...c,
      vendedor: vendedorMap[c.vendedor_id] || null
    }))
  }
}

/** Total de não lidas e conversa mais recente com mensagem nova, só do vendedor. */
export async function resumoInboxVendedor(vendedorId) {
  if (!vendedorId) {
    return { success: true, totalNaoLidas: 0, conversaDestaqueId: null }
  }

  const res = await listarConversas({ vendedorId, isGestor: false })
  if (!res.success) {
    return { success: false, error: res.error, totalNaoLidas: 0, conversaDestaqueId: null }
  }

  const comNaoLidas = (res.conversas || [])
    .filter((c) => Number(c.unread_count) > 0)
    .sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0))

  const totalNaoLidas = comNaoLidas.reduce((soma, c) => soma + Number(c.unread_count || 0), 0)
  return {
    success: true,
    totalNaoLidas,
    conversaDestaqueId: comNaoLidas[0]?.id || null
  }
}

export async function buscarConversaPorId(conversaId) {
  if (!conversaId) return { success: false, conversa: null }

  const { data, error } = await supabase
    .from('whatsapp_conversas')
    .select(`
      id,
      wa_id,
      telefone,
      contato_nome,
      vendedor_id,
      status,
      last_message_at,
      last_message_preview,
      unread_count,
      first_inbound_at,
      first_response_at,
      created_at
    `)
    .eq('id', conversaId)
    .maybeSingle()

  if (error || !data) {
    return { success: false, error: error?.message, conversa: null }
  }

  let vendedor = null
  if (data.vendedor_id) {
    const { data: vend } = await supabase
      .from('usuarios_sistema')
      .select('id, nome')
      .eq('id', data.vendedor_id)
      .maybeSingle()
    vendedor = vend || null
  }

  const [comAgenda] = await aplicarContatosSalvos([data])

  return { success: true, conversa: { ...comAgenda, vendedor } }
}

export async function listarMensagens(conversaId) {
  const { data: atual } = await supabase
    .from('whatsapp_conversas')
    .select('id, wa_id, telefone')
    .eq('id', conversaId)
    .maybeSingle()

  let ids = [conversaId]
  if (atual) {
    const chave = localKeyContato(atual.wa_id || atual.telefone)
    if (chave) {
      const { data: irmas } = await supabase
        .from('whatsapp_conversas')
        .select('id, wa_id, telefone')
      ids = [...new Set(
        (irmas || [])
          .filter((c) => localKeyContato(c.wa_id) === chave || localKeyContato(c.telefone) === chave)
          .map((c) => c.id)
          .concat(conversaId)
      )]
    }
  }

  const { data, error } = await supabase
    .from('whatsapp_mensagens')
    .select(`
      id,
      conversa_id,
      direction,
      vendedor_id,
      message_id,
      message_type,
      body,
      status,
      payload,
      created_at
    `)
    .in('conversa_id', ids)
    .order('created_at', { ascending: true })

  if (error) {
    console.error('[WhatsApp CRM] Erro ao listar mensagens:', error)
    return { success: false, error: error.message, mensagens: [] }
  }

  const mensagens = data || []
  const vendedorIds = [...new Set(mensagens.map((m) => m.vendedor_id).filter(Boolean))]
  let vendedorMap = {}

  if (vendedorIds.length > 0) {
    const { data: vendedores } = await supabase
      .from('usuarios_sistema')
      .select('id, nome')
      .in('id', vendedorIds)
    vendedorMap = Object.fromEntries((vendedores || []).map((v) => [v.id, v]))
  }

  return {
    success: true,
    mensagens: mensagens.map((m) => ({
      ...m,
      vendedor: m.vendedor_id ? vendedorMap[m.vendedor_id] || null : null
    }))
  }
}

export async function marcarConversaLida(conversaId) {
  const { error } = await supabase
    .from('whatsapp_conversas')
    .update({ unread_count: 0 })
    .eq('id', conversaId)

  if (error) {
    return { success: false, error: error.message }
  }
  return { success: true }
}

export async function encerrarConversa(conversaId) {
  const { error } = await supabase
    .from('whatsapp_conversas')
    .update({ status: 'encerrada' })
    .eq('id', conversaId)

  if (error) {
    return { success: false, error: error.message }
  }
  return { success: true }
}

export async function salvarContatoConversa({ conversaId, nome, usuarioId }) {
  const nomeLimpo = String(nome || '').trim()
  if (!conversaId || !nomeLimpo) {
    return { success: false, error: 'Informe o nome do contato.' }
  }

  const { data: atual, error: errAtual } = await supabase
    .from('whatsapp_conversas')
    .select('id, wa_id, telefone, empresa_id')
    .eq('id', conversaId)
    .maybeSingle()

  if (errAtual || !atual) {
    return { success: false, error: errAtual?.message || 'Conversa não encontrada.' }
  }

  const chave = localKeyContato(atual.wa_id || atual.telefone)
  const ids = new Set([conversaId])
  if (chave) {
    const { data: irmas } = await supabase
      .from('whatsapp_conversas')
      .select('id, wa_id, telefone')
    ;(irmas || []).forEach((c) => {
      if (localKeyContato(c.wa_id) === chave || localKeyContato(c.telefone) === chave) {
        ids.add(c.id)
      }
    })
  }

  const { error } = await supabase
    .from('whatsapp_conversas')
    .update({ contato_nome: nomeLimpo })
    .in('id', [...ids])

  if (error) {
    return { success: false, error: error.message }
  }

  await supabase
    .from('whatsapp_conversas')
    .update({ contato_nome_salvo: true })
    .in('id', [...ids])

  if (chave) {
    const { data: existente, error: errAgenda } = await supabase
      .from('whatsapp_contatos')
      .select('id')
      .eq('telefone_chave', chave)
      .maybeSingle()

    if (errAgenda) {
      return {
        success: false,
        error: 'Execute o SQL 157_whatsapp_salvar_contato.sql no Supabase para ativar a agenda de contatos.'
      }
    }

    if (existente?.id) {
      await supabase
        .from('whatsapp_contatos')
        .update({
          nome: nomeLimpo,
          telefone: atual.telefone,
          usuario_id: usuarioId || null
        })
        .eq('id', existente.id)
    } else {
      const { error: errInsert } = await supabase.from('whatsapp_contatos').insert({
        telefone_chave: chave,
        telefone: atual.telefone,
        nome: nomeLimpo,
        empresa_id: atual.empresa_id || null,
        usuario_id: usuarioId || null
      })
      if (errInsert) {
        return { success: false, error: errInsert.message }
      }
    }
  }

  return { success: true, nome: nomeLimpo }
}

export async function enviarRespostaCrm({
  usuarioId,
  conversaId,
  message,
  type = 'text',
  caption,
  filename,
  mimeType,
  fileBase64,
  mensagemOrigemId
}) {
  try {
    const body = {
      usuario_id: usuarioId,
      conversa_id: conversaId,
      type
    }
    if (message) body.message = message
    if (caption) body.caption = caption
    if (type === 'media') {
      body.filename = filename
      body.mime_type = mimeType
      body.file_base64 = fileBase64
    }
    if (type === 'forward') {
      body.mensagem_origem_id = mensagemOrigemId
    }

    const result = await invokeEdgeFunction('whatsapp-crm-send', body)
    const data = result?.data
    const error = result?.error

    if (data && (data.error || data.success === false)) {
      return { success: false, error: data.error || 'Erro ao enviar.', details: data.details }
    }

    if (error) {
      const msg = await extrairErroEdgeFunction(error, data)
      return { success: false, error: msg }
    }

    return { success: true, message_id: data?.message_id, message: data?.message || 'Enviado.' }
  } catch (e) {
    console.error('[WhatsApp CRM] Exceção ao enviar:', e)
    return { success: false, error: (e && e.message) || 'Falha ao enviar mensagem.' }
  }
}

export async function carregarRelatorioGestor() {
  const { data: conversas, error: convError } = await supabase
    .from('whatsapp_conversas')
    .select('id, vendedor_id, first_inbound_at, first_response_at, created_at')

  if (convError) {
    return { success: false, error: convError.message, relatorio: [] }
  }

  const { data: mensagens, error: msgError } = await supabase
    .from('whatsapp_mensagens')
    .select('id, conversa_id, direction, vendedor_id, created_at')

  if (msgError) {
    return { success: false, error: msgError.message, relatorio: [] }
  }

  const { data: vendedores } = await supabase
    .from('usuarios_sistema')
    .select('id, nome')
    .eq('status', 'ativo')
    .eq('chat_zap', 1)

  const vendedorMap = Object.fromEntries((vendedores || []).map((v) => [v.id, v.nome]))
  const stats = {}

  for (const v of vendedores || []) {
    stats[v.id] = {
      vendedor_id: v.id,
      vendedor_nome: v.nome,
      conversas_ativas: 0,
      mensagens_enviadas: 0,
      mensagens_recebidas: 0,
      tempo_resposta_medio_seg: null
    }
  }

  const temposResposta = {}

  for (const c of conversas || []) {
    if (!c.vendedor_id || !stats[c.vendedor_id]) continue
    if (c.first_inbound_at && !c.first_response_at) {
      stats[c.vendedor_id].conversas_ativas += 1
    } else if (c.first_response_at) {
      stats[c.vendedor_id].conversas_ativas += 1
      const diff = (new Date(c.first_response_at).getTime() - new Date(c.first_inbound_at || c.created_at).getTime()) / 1000
      if (diff >= 0) {
        if (!temposResposta[c.vendedor_id]) temposResposta[c.vendedor_id] = []
        temposResposta[c.vendedor_id].push(diff)
      }
    } else {
      stats[c.vendedor_id].conversas_ativas += 1
    }
  }

  for (const m of mensagens || []) {
    const conv = (conversas || []).find((c) => c.id === m.conversa_id)
    const vid = m.vendedor_id || conv?.vendedor_id
    if (!vid || !stats[vid]) continue
    if (m.direction === 'outbound') stats[vid].mensagens_enviadas += 1
    if (m.direction === 'inbound') stats[vid].mensagens_recebidas += 1
  }

  const relatorio = Object.values(stats).map((s) => {
    const tempos = temposResposta[s.vendedor_id] || []
    const media = tempos.length
      ? Math.round(tempos.reduce((a, b) => a + b, 0) / tempos.length)
      : null
    return { ...s, tempo_resposta_medio_seg: media }
  })

  relatorio.sort((a, b) => a.vendedor_nome.localeCompare(b.vendedor_nome))

  return { success: true, relatorio, vendedorMap }
}

export function formatarTempoResposta(segundos) {
  if (segundos == null) return '—'
  if (segundos < 60) return `${segundos}s`
  if (segundos < 3600) return `${Math.round(segundos / 60)} min`
  return `${Math.round(segundos / 3600)} h`
}

export function formatarTelefoneExibicao(telefone) {
  const d = String(telefone || '').replace(/\D/g, '')
  if (d.length >= 13) {
    return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`
  }
  if (d.length === 12) {
    return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`
  }
  if (d.length === 11) {
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  }
  if (d.length === 10) {
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  }
  return telefone || '—'
}

function normalizePhoneLocal(raw) {
  let digits = String(raw || '').replace(/\D/g, '')
  if (digits.startsWith('0')) digits = digits.replace(/^0+/, '')
  if (digits.length === 10 || digits.length === 11) digits = `55${digits}`
  return digits
}

/** DDD + 8 dígitos, ignorando o 9 extra do celular. */
function localKeyContato(raw) {
  const n = normalizePhoneLocal(raw)
  const local = n.startsWith('55') ? n.slice(2) : n
  if (local.length === 11 && local[2] === '9') {
    return local.slice(0, 2) + local.slice(3)
  }
  if (local.length >= 10) return local.slice(-10)
  return local
}

async function aplicarContatosSalvos(conversas) {
  if (!conversas?.length) return conversas || []
  const { data, error } = await supabase
    .from('whatsapp_contatos')
    .select('telefone_chave, nome')
  if (error || !data?.length) {
    return conversas.map((c) => ({ ...c, contato_nome_salvo: Boolean(c.contato_nome_salvo) }))
  }
  const mapa = Object.fromEntries(data.map((r) => [r.telefone_chave, r.nome]))
  return conversas.map((c) => {
    const chave = localKeyContato(c.wa_id || c.telefone)
    const nomeSalvo = chave ? mapa[chave] : null
    if (!nomeSalvo) return { ...c, contato_nome_salvo: Boolean(c.contato_nome_salvo) }
    return { ...c, contato_nome: nomeSalvo, contato_nome_salvo: true }
  })
}

function scoreConversaContato(c) {
  const n = Math.max(
    normalizePhoneLocal(c.wa_id).length,
    normalizePhoneLocal(c.telefone).length
  )
  return (n >= 13 ? 4 : 0) + (c.first_response_at ? 2 : 0)
}

function agruparConversasPorContato(conversas) {
  const grupos = new Map()
  for (const c of conversas) {
    const chave = localKeyContato(c.wa_id || c.telefone) || c.id
    const atual = grupos.get(chave)
    if (!atual) {
      grupos.set(chave, { ...c, idsRelacionados: [c.id] })
      continue
    }
    const preferirNova = scoreConversaContato(c) > scoreConversaContato(atual)
      || (scoreConversaContato(c) === scoreConversaContato(atual)
        && new Date(c.last_message_at || 0) > new Date(atual.last_message_at || 0))
    const idsRelacionados = [...new Set([...(atual.idsRelacionados || [atual.id]), c.id])]
    const unread = (Number(atual.unread_count) || 0) + (Number(c.unread_count) || 0)
    const base = preferirNova ? c : atual
    const preview = new Date(c.last_message_at || 0) > new Date(atual.last_message_at || 0)
      ? c
      : atual
    grupos.set(chave, {
      ...base,
      idsRelacionados,
      unread_count: unread,
      last_message_at: preview.last_message_at,
      last_message_preview: preview.last_message_preview,
      contato_nome_salvo: Boolean(atual.contato_nome_salvo || c.contato_nome_salvo),
      contato_nome: (atual.contato_nome_salvo && atual.contato_nome)
        || (c.contato_nome_salvo && c.contato_nome)
        || base.contato_nome
    })
  }
  return [...grupos.values()].sort(
    (a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)
  )
}

function coletarMensagensPayload(payload) {
  const lista = []
  const visit = (node) => {
    if (!node || typeof node !== 'object') return
    if (Array.isArray(node)) {
      node.forEach(visit)
      return
    }
    if (Array.isArray(node.messages)) lista.push(...node.messages.filter(Boolean))
    if (Array.isArray(node.entry)) visit(node.entry)
    if (Array.isArray(node.changes)) visit(node.changes)
    if (node.value) visit(node.value)
  }
  visit(payload)
  return lista
}

export function extrairMidiaMensagem(payload, messageId) {
  if (payload && typeof payload === 'object' && payload.media_id) {
    return {
      mediaId: payload.media_id,
      mimeType: payload.mime_type || '',
      voice: Boolean(payload.voice),
      type: payload.wa_type || payload.message_type || 'document',
      filename: payload.filename || '',
      caption: payload.caption || ''
    }
  }
  const mensagens = coletarMensagensPayload(payload)
  const msg = (messageId ? mensagens.find((m) => m.id === messageId) : null) || mensagens[0]
  if (!msg) return null
  const tipo = typeof msg.type === 'string' ? msg.type : ''
  const bloco = (tipo && msg[tipo]) || msg.audio || msg.voice || msg.image || msg.video || msg.document
  const id = bloco?.id
  if (!id) return null
  return {
    mediaId: id,
    mimeType: bloco.mime_type || '',
    voice: Boolean(bloco.voice),
    type: tipo || 'audio',
    filename: bloco.filename || '',
    caption: bloco.caption || ''
  }
}

export function isMensagemAudio(mensagem) {
  const tipo = String(mensagem?.message_type || '').toLowerCase()
  if (tipo && tipo !== 'audio' && tipo !== 'voice' && tipo !== 'ptt') return false
  if (tipo === 'audio' || tipo === 'voice' || tipo === 'ptt') return true
  const midia = extrairMidiaMensagem(mensagem?.payload, mensagem?.message_id)
  if (midia?.type === 'audio' || midia?.voice) return true
  return /🎤 Áudio|\[audio\]/i.test(String(mensagem?.body || ''))
}

export function isMensagemImagem(mensagem) {
  const tipo = String(mensagem?.message_type || '').toLowerCase()
  if (tipo && tipo !== 'image') return false
  if (tipo === 'image') return true
  const midia = extrairMidiaMensagem(mensagem?.payload, mensagem?.message_id)
  return midia?.type === 'image' || /📷 Foto|\[image\]/i.test(String(mensagem?.body || ''))
}

export function isMensagemVideo(mensagem) {
  const tipo = String(mensagem?.message_type || '').toLowerCase()
  if (tipo && tipo !== 'video') return false
  if (tipo === 'video') return true
  const midia = extrairMidiaMensagem(mensagem?.payload, mensagem?.message_id)
  return midia?.type === 'video' || /🎥 Vídeo|\[video\]/i.test(String(mensagem?.body || ''))
}

export const TEXTO_MENSAGEM_INICIAL_WHATSAPP =
  'Mensagem inicial enviada, aguarde o contato responder.'

function pareceNomeTecnicoTemplate(texto) {
  const t = String(texto || '').trim()
  if (!t) return true
  const lower = t.toLowerCase()
  if (lower === 'template enviado' || lower.startsWith('[')) return true
  return /^[a-z0-9]+(_[a-z0-9]+)+$/i.test(t)
}

export function textoMensagemWhatsApp(mensagem) {
  const tipo = String(mensagem?.message_type || '').toLowerCase()
  if (mensagem?.direction === 'outbound' && tipo === 'template' && pareceNomeTecnicoTemplate(mensagem.body)) {
    return TEXTO_MENSAGEM_INICIAL_WHATSAPP
  }
  return mensagem?.body || (mensagem?.message_type ? `[${mensagem.message_type}]` : '')
}

export function textoPreviewConversaWhatsApp(preview) {
  const t = String(preview || '').trim()
  if (!t) return 'Sem mensagens'
  if (pareceNomeTecnicoTemplate(t)) return TEXTO_MENSAGEM_INICIAL_WHATSAPP
  return t
}

export function isMensagemDocumento(mensagem) {
  const tipo = String(mensagem?.message_type || '').toLowerCase()
  if (tipo && tipo !== 'document') return false
  if (tipo === 'document') return true
  const midia = extrairMidiaMensagem(mensagem?.payload, mensagem?.message_id)
  return midia?.type === 'document' || /📄 |\[document\]/i.test(String(mensagem?.body || ''))
}

export function mensagemFoiEncaminhada(mensagem) {
  return Boolean(mensagem?.payload?.forwarded)
}

export const WHATSAPP_MAX_ARQUIVO_BYTES = Math.floor(3.5 * 1024 * 1024)

export function arquivoParaBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const s = String(reader.result || '')
      const comma = s.indexOf(',')
      resolve(comma >= 0 ? s.slice(comma + 1) : s)
    }
    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'))
    reader.readAsDataURL(file)
  })
}

/** Baixa o arquivo de mídia (áudio) via Edge Function e devolve um Blob. */
export async function baixarMidiaWhatsApp({ usuarioId, mensagemId }) {
  if (!usuarioId || !mensagemId) {
    return { success: false, error: 'Informe o usuário e a mensagem.' }
  }

  const headers = { 'Content-Type': 'application/json' }
  let url = '/api/whatsapp-media'
  if (!USE_DEV_EDGE_PROXY) {
    url = `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/whatsapp-media`
    if (SUPABASE_ANON_KEY) {
      headers.Authorization = `Bearer ${SUPABASE_ANON_KEY}`
      headers.apikey = SUPABASE_ANON_KEY
    }
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ usuario_id: usuarioId, mensagem_id: mensagemId })
    })
    const contentType = res.headers.get('content-type') || ''
    if (!res.ok || contentType.includes('application/json')) {
      const data = await res.json().catch(() => ({}))
      return {
        success: false,
        error: data.error || `Não foi possível carregar o áudio (HTTP ${res.status}).`
      }
    }
    const blob = await res.blob()
    return { success: true, blob, mimeType: contentType }
  } catch (e) {
    console.error('[WhatsApp CRM] Erro ao baixar mídia:', e)
    return { success: false, error: (e && e.message) || 'Falha ao baixar o áudio.' }
  }
}
